import {
    getLangObjects,
    LanguageCode,
    LoggedPage,
    NativeLanguageDTO,
    PrepCheckDraftDetails,
    PrepCheckResultOverview,
    PrepCheckStatus,
    sortPrepChecks,
    SupportedHazardFilter,
} from '@hazadapt-git/public-core-base'
import * as clipboard from 'clipboard-polyfill'
import React, { FC } from 'react'
import { IoIosLink } from 'react-icons/io'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    PrepCheckCardBaseProps,
    PrepCheckLandingPageTemplate,
} from '../components'
import { getEnvVars } from '../lib/config'
import { HazardFilter, PageProps } from '../lib/entities'
import {
    getAllPrepChecksThunk,
    getPrepCheckUserStatsThunk,
    saveProfileChangesThunk,
    selectIsAppReady,
    selectLanguage,
    selectPrepChecks,
    selectPrepCheckUserStats,
    selectSupportedLanguages,
    selectUser,
    switchLanguageThunk,
} from '../lib/slices'
import { useAppDispatch, useAppSelector } from '../lib/store'
import {
    successColor,
    primaryIconSize,
    errorColor,
} from '../lib/styles/universal'
import {
    logEvent,
    toast,
    useWindowSizeUp,
    getAllPrepCheckResults,
    getAllSubmissions,
    canGuestAccessPrepCheck,
    signup,
    markGuestTutorialShown,
    getGuestSubmissionSummaries,
    getGuestResultSummaries,
    getGuestStartedPrepCheckIds,
    getDefaultGuestStats,
} from '../lib/utils'
import PrepCheckPlaceholderBadge from '../assets/icons/PrepCheckPlaceholderBadge.svg'
import { IoCloudDownloadOutline, IoLanguage } from 'react-icons/io5'
import { shallowEqual } from 'react-redux'
import { getSessionItem, setSessionItem } from '../lib/browser-storage'

interface PrepCheckLandingPageProps extends PageProps {}

const env = getEnvVars()
export const PrepCheckLandingPage: FC<PrepCheckLandingPageProps> = (
    props: PrepCheckLandingPageProps
) => {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const mediumWindowOrLarger = useWindowSizeUp('md')

    const [prepChecksRetrieved, setPrepChecksRetrieved] =
        React.useState<boolean>(false)
    const [userStatsRetrieved, setUserStatsRetrieved] =
        React.useState<boolean>(false)
    const [resultsRetrieved, setResultRetrieved] =
        React.useState<boolean>(false)
    const [submissionsRetrieved, setSubmissionsRetrieved] =
        React.useState<boolean>(false)
    const [filtersExpanded, setFiltersExpanded] = React.useState<boolean>(false)

    const supportedLanguages = useAppSelector(
        selectSupportedLanguages,
        shallowEqual
    )
    const prepChecks = useAppSelector(selectPrepChecks, shallowEqual)
    const prepCheckStats = useAppSelector(
        selectPrepCheckUserStats,
        shallowEqual
    )
    const language = useAppSelector(selectLanguage)
    const user = useAppSelector(selectUser, shallowEqual)
    const appReady = useAppSelector(selectIsAppReady)

    const [cards, setCards] = React.useState<PrepCheckCardBaseProps[]>([])
    const [results, setResults] = React.useState<PrepCheckResultOverview[]>([])
    const [submissions, setSubmissions] = React.useState<
        PrepCheckDraftDetails[]
    >([])
    const [filters, setFilters] = React.useState<Set<SupportedHazardFilter>>(
        new Set<SupportedHazardFilter>()
    )
    const [activeFilters, setActiveFilters] = React.useState<
        SupportedHazardFilter[]
    >([])
    const [languageSelectorModalOpen, setLanguageSelectorModalOpen] =
        React.useState<boolean>(false)
    const [pendingPrepCheckId, setPendingPrepCheckId] = React.useState<
        number | null
    >(null)
    const [showWelcomeModal, setShowWelcomeModal] =
        React.useState<boolean>(false)
    const [guestSubmissions, setGuestSubmissions] = React.useState<
        PrepCheckDraftDetails[]
    >([])
    const [guestResults, setGuestResults] = React.useState<
        Partial<PrepCheckResultOverview>[]
    >([])
    const [showPrepCheckLimitWarning, setShowPrepCheckLimitWarning] =
        React.useState<boolean>(false)

    React.useEffect(() => {
        document.title = 'Prep Checks - HazAdapt'
    }, [])

    // Show welcome modal, once for auth users and once per session for guests
    React.useEffect(() => {
        if (appReady) {
            const hasSeenModal = user
                ? user.tutorials_shown.includes('PrepCheckWelcome')
                : getSessionItem('hazadapt_guest_welcome_seen')
            if (!hasSeenModal) {
                setShowWelcomeModal(true)
            }
        }
    }, [appReady, user])

    React.useEffect(() => {
        if (!user) {
            setGuestSubmissions(getGuestSubmissionSummaries())
            setGuestResults(getGuestResultSummaries())
        } else {
            setGuestSubmissions([])
            setGuestResults([])
        }
    }, [user, location.pathname])

    React.useEffect(() => {
        if (!appReady) return

        logEvent('OPEN_PAGE', {
            page: LoggedPage.PREP_CHECK_LANDING,
            language,
        })

        dispatch(getAllPrepChecksThunk(language))
            .then(() => setPrepChecksRetrieved(true))
            .catch(console.error)

        if (user) {
            dispatch(getPrepCheckUserStatsThunk(language))
                .then(() => setUserStatsRetrieved(true))
                .catch(console.error)

            // Get results and draft submissions
            getAllPrepCheckResults()
                .then(setResults)
                .catch(console.error)
                .finally(() => {
                    setResultRetrieved(true)
                })
            getAllSubmissions()
                .then(setSubmissions)
                .catch(console.error)
                .finally(() => {
                    setSubmissionsRetrieved(true)
                })
        }
    }, [language, dispatch, appReady, user])

    React.useEffect(() => {
        const filters: Set<SupportedHazardFilter> =
            new Set<SupportedHazardFilter>()
        for (const pc of prepChecks) {
            for (const f of pc.filters) {
                filters.add(f)
            }
        }
        setFilters(filters)
    }, [prepChecks])

    React.useEffect(() => {
        const cards: PrepCheckCardBaseProps[] = []
        const sortedPrepChecks = sortPrepChecks(prepChecks, activeFilters)
        // Use guest or auth submissions/results based on user
        const userSubmissions = user ? submissions : guestSubmissions
        const userResults = user ? results : guestResults
        const guestStartedIds = !user ? getGuestStartedPrepCheckIds() : []
        for (const pc of sortedPrepChecks) {
            if (
                pc.filters.length > 0 &&
                activeFilters.length > 0 &&
                !pc.filters.some((f) => activeFilters.includes(f))
            ) {
                // Prep check does not include active filters; don't show
                continue
            }

            // Check for user's submission (draft for auth, submission for guest)
            const userSubmission = userSubmissions.find((d) => d.id === pc.id)
            const userResult = userResults.find(
                (r) => r.prep_check_id === pc.id
            )

            let progress: number = 0
            let status: PrepCheckStatus | undefined
            const startedByGuest = !user && guestStartedIds.includes(pc.id)

            if (userResult) {
                status = PrepCheckStatus.SUBMITTED
            } else if (userSubmission && (userSubmission.amountDone ?? 0) < 1) {
                // User has draft/submission in progress
                progress = userSubmission.amountDone ?? 0
                status = PrepCheckStatus.DRAFT
            }

            cards.push({
                id: pc.id,
                title: pc.title,
                dark_icon: pc.dark_icon?.url ?? pc.dark_icon?.src ?? '',
                filterChips: pc.filters
                    .filter((f) => activeFilters.includes(f))
                    .map((f) => HazardFilter[f]),
                status,
                progressBar: progress,
                score: userResult?.score,
                badge: userResult
                    ? userResult?.badge
                    : {
                          src: PrepCheckPlaceholderBadge,
                          alt: 'Plain dark gray empty badge.',
                      },
                startedByGuest,
            })
        }
        setCards(cards)
    }, [
        activeFilters,
        submissions,
        guestSubmissions,
        language,
        prepChecks,
        results,
        guestResults,
        user,
    ])

    const onPrepCheckClick = (id: number, done = false) => {
        if (!user) {
            if (!canGuestAccessPrepCheck(id)) {
                // Guest is at limit and trying to start a new prep check
                setPendingPrepCheckId(id)
                setShowPrepCheckLimitWarning(true)
                return
            }
        }
        if (done) {
            navigate(`/prep-checks/${id}/results`, {
                state: { allowBack: true },
            })
            return
        }

        navigate(`/prep-checks/${id}?from=${location.pathname}`)
    }

    const onPrepCheckLimitWarningModalClose = () => {
        setShowPrepCheckLimitWarning(false)
        setPendingPrepCheckId(null)
    }

    const onSignUpClick = async () => {
        const redirectUrl = pendingPrepCheckId
            ? `${env.frontendUriBase}/prep-checks/${pendingPrepCheckId}`
            : `${env.frontendUriBase}/prep-checks`
        await signup(redirectUrl)
    }

    const onJourneyCompletionClick: React.MouseEventHandler = (e) => {
        navigate('/prep-checks')
    }

    const onMyEmergencyItemsClick: React.MouseEventHandler = (e) => {
        navigate('/my-emergency-items')
    }

    const onFilterClick = (filter: SupportedHazardFilter) => {
        const active_filters = [...activeFilters]
        const idx = active_filters.findIndex((f) => f === filter)
        if (idx >= 0) {
            active_filters.splice(idx, 1)
        } else {
            active_filters.unshift(filter)
        }
        setActiveFilters(active_filters)
    }

    const onResetFilters = () => {
        setActiveFilters([])
    }

    const onSharePress = (e: React.MouseEvent, id: number) => {
        e.preventDefault()
        e.stopPropagation()
        const prepCheck = prepChecks.find((pc) => pc.id === id)
        if (!prepCheck) return

        logEvent('SHARE_PREP_CHECK', {
            prep_check_id: id,
            name: prepCheck.title,
            page: 'PrepCheckLanding',
        })
        const link = `${env.hazGuideQuickUriBase}/pc/${id}`
        clipboard
            .writeText(link)
            .then(() => {
                toast(
                    'Link copied!',
                    <IoIosLink color={successColor} size={primaryIconSize} />
                )
            })
            .catch(console.error)
    }

    const onPrepCheckWelcomeClose = () => {
        if (user) {
            const tutorials_shown = new Set<string>(user.tutorials_shown)
            tutorials_shown.add('PrepCheckWelcome')
            dispatch(
                saveProfileChangesThunk({
                    tutorials_shown: Array.from(tutorials_shown),
                })
            )
        } else {
            setShowWelcomeModal(false)
            markGuestTutorialShown('PrepCheckWelcome')
            setSessionItem('hazadapt_guest_welcome_seen', 'true')
        }
    }

    const onLanguageChange = async (lang: LanguageCode) => {
        const langObject: NativeLanguageDTO = getLangObjects([lang])[0]
        setLanguageSelectorModalOpen(false)
        toast(
            `Downloading Prep Checks in ${langObject.title}.`,
            <IoCloudDownloadOutline
                color={successColor}
                size={primaryIconSize}
            />
        )
        try {
            await dispatch(switchLanguageThunk({ lang }))
            dispatch(getPrepCheckUserStatsThunk(lang))
            await dispatch(getAllPrepChecksThunk(lang))
            toast(
                `Prep Checks have been translated into ${langObject.title}.`,
                <IoLanguage color={successColor} size={primaryIconSize} />
            )
        } catch (err) {
            console.error(err)
            toast(
                `Unable to download Prep Checks in ${langObject.title}.`,
                <IoLanguage color={errorColor} size={primaryIconSize} />
            )
        }
    }

    const onTranslateClick: React.MouseEventHandler = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setLanguageSelectorModalOpen(true)
    }

    const onLanguageSelectorModalClose = () => {
        setLanguageSelectorModalOpen(false)
    }

    const scrollY = React.useRef(0)
    React.useEffect(() => {
        if (mediumWindowOrLarger) {
            if (filtersExpanded) setFiltersExpanded(false)
            return
        }
        const closeFilterViewOnScrollDown = () => {
            if (!filtersExpanded || !mediumWindowOrLarger)
                if (scrollY.current < window.scrollY) {
                    setFiltersExpanded(false)
                }
            scrollY.current = window.scrollY
        }

        window.addEventListener('scroll', closeFilterViewOnScrollDown)
        return () => {
            window.removeEventListener('scroll', closeFilterViewOnScrollDown)
        }
    }, [filtersExpanded, mediumWindowOrLarger])

    const displayStats = user
        ? prepCheckStats
        : getDefaultGuestStats('prep-check-landing')

    return (
        <PrepCheckLandingPageTemplate
            {...displayStats}
            totalPrepChecks={displayStats.numTotal}
            prepChecksCompleted={displayStats.numCompleted}
            activeFilters={activeFilters}
            onChipPress={onFilterClick}
            onChipReset={onResetFilters}
            prepChecks={cards}
            filters={Array.from(filters).map((f) => HazardFilter[f])}
            onPrepCheckClick={onPrepCheckClick}
            onJourneyCompletionClick={onJourneyCompletionClick}
            onMyEmergencyItemsClick={onMyEmergencyItemsClick}
            onSharePress={onSharePress}
            loggedIn={!!user}
            loading={
                props.loading ||
                !prepChecksRetrieved || // prep checks have not been retrieved yet
                (!!user && // user is logged in but their prep check-related info is not all retrieved yet
                    (!userStatsRetrieved ||
                        !resultsRetrieved ||
                        !submissionsRetrieved))
            }
            showPrepCheckWelcome={showWelcomeModal}
            onPrepCheckWelcomeClose={onPrepCheckWelcomeClose}
            languages={supportedLanguages}
            selectedLanguage={language}
            onLanguageChange={onLanguageChange}
            onLanguageSelectorModalClose={onLanguageSelectorModalClose}
            languageSelectorModalOpen={languageSelectorModalOpen}
            onTranslateClick={onTranslateClick}
            filtersExpanded={filtersExpanded}
            onFilterViewToggle={setFiltersExpanded}
            showPrepCheckLimitWarning={showPrepCheckLimitWarning}
            onPrepCheckLimitWarningModalClose={
                onPrepCheckLimitWarningModalClose
            }
            onSignUpClick={onSignUpClick}
        />
    )
}
